import type { Options } from "ky";
import useSWRMutation from "swr/mutation";
import { clientFetchMutation } from "@/lib";
import { BaseUrl } from "@/types/base-url";

/**
 * Custom hook wrapper for SWR mutations with automatic clientFetchMutation integration
 *
 * This hook simplifies the usage of useSWRMutation by automatically providing
 * the clientFetchMutation function with the specified base URL type.
 *
 * @param url - The API endpoint URL
 * @param type - The base URL type (default: BaseUrl.DEFAULT)
 *
 * @returns SWR mutation object with trigger, isMutating, error, etc.
 *
 * @example
 * ```tsx
 * // Basic POST request
 * const { trigger, isMutating } = useMutation<ResponseType<Product>>("/products");
 *
 * await trigger({
 *   method: "POST",
 *   json: { name: "New Product", price: 100000 }
 * });
 *
 * // PUT request
 * const { trigger } = useMutation<ResponseType<Product>>("/products/123");
 *
 * await trigger({
 *   method: "PUT",
 *   json: { name: "Updated Product" }
 * });
 *
 * // DELETE request
 * const { trigger } = useMutation<ResponseType<void>>("/products/123");
 *
 * await trigger({ method: "DELETE" });
 *
 * // Using different base URL
 * const { trigger } = useMutation<ResponseType<Notification>>(
 *   "/notifications",
 *   BaseUrl.NOTIFICATION
 * );
 *
 * await trigger({
 *   method: "POST",
 *   json: { message: "Hello" }
 * });
 * ```
 */
export default function useMutation<R>(
	url: string,
	type: BaseUrl = BaseUrl.DEFAULT,
) {
	return useSWRMutation<R, Error, string, Options>(url, (url, options) =>
		clientFetchMutation(url, options, type),
	);
}
